import * as React from 'react'
import styled from 'styled-components'
import { Form, Input, Spin, Button, TreeSelect, Tooltip, message } from 'antd'
import { set, cloneDeep, isEmpty, get } from 'lodash'
import { Base64 } from 'js-base64'
import Select, { Option, getOptionProps } from 'elements/Select'
import DatePicker from 'elements/DatePicker'
import Checkbox from 'elements/Checkbox'
import Icon from 'elements/Icon'
import * as CSV from 'csv-string'
import produce from 'immer'
import { strEqual } from 'helpers/utils'
import { showError, resourceNotFound, ValidationError } from 'helpers/errors'
import { createLabelFactory, createFieldFactory, createHandleSubmit, isReadOnly } from 'helpers/formViews'
import { t } from 'helpers/i18n'
import { filterTreeNode } from 'helpers/trees'
import { stopEvent } from 'helpers/events'
import FileUpload from 'elements/FileUpload'
import Drawer from 'elements/Drawer'

const { TreeNode } = TreeSelect

const Container = styled.div`
  .ant-select-disabled .ant-select-selection {
    background-color: white !important;
  }

  .ant-select-disabled .ant-select-selection--multiple .ant-select-selection__choice {
    color: #666 !important;
    background-color: #fafafa !important;
  }
`

class FormView extends React.Component {
  state = {}

  constructor(props) {
    super(props)

    this.handleSubmit = createHandleSubmit(this)
  }

  componentDidMount() {
    this.fetchItem()
  }

  getItemId = () => [this.state.item?.id, this.props.linkTargetRecord?.formBatchId].find(Boolean)

  fetchItem = async ({ itemId = this.getItemId() } = {}) => {
    const getCustomers = this.props.tenantIsFnl
      ? this.props.getCustomersHierarchy
      : this.props.getCustomersList

    try {
      const responses = await Promise.all([
        itemId
          ? this.props.getItem(itemId).catch((error) => {
              this.props.onCancel?.()
              throw error
            })
          : !this.props.linkTargetRecord
            ? this.props.newItem({})
            : resourceNotFound(this),
        this.props.getSettings({ type: this.props.settingsType }),
        getCustomers(),
        this.props.getFormTemplates(),
      ])

      const item = cloneDeep(responses[0].value.data)

      this.setState({
        item,
        fieldSettings: responses[1].value.data.fieldSettings,
        customers: get(responses[2], 'value.data.items', []),
        formTemplates: get(responses[3], 'value.data.items', []),
      })
    } catch (error) {
      showError({ error })
    }
  }

  setItemValue = (name, value) =>
    this.setState(
      produce((draft) => {
        set(draft.item, name, value)

        this.props.form.setFieldsValue({ [name]: value })
      })
    )

  handleFileUpload = (fileList = []) => {
    if (fileList[0]) {
      const customers = this.state.customers
        .flatMap((each) => each.districts)
        .flatMap((each) => each.customers)
      const customerNames = CSV.parse(Base64.decode(fileList[0].fileContents))
        .map((each) => each[0])
        .filter(Boolean)
      const customerIds = customerNames
        .map((each) => customers.find((one) => strEqual(one.name, each)))
        .filter(Boolean)
        .map((each) => each.id)
      const type =
        !isEmpty(customerNames) || isEmpty(customerIds)
          ? 'error'
          : customerIds.length < customerNames.length
            ? 'warn'
            : 'success'

      message[type](`${customerIds.length} ${t('of')} ${customerNames.length} ${t('itemsAdded')}`)

      this.setItemValue('customerIds', [...new Set(customerIds)])
      this.setState({ fileUploadDrawerVisible: false })
    }
  }

  render() {
    const { item, fieldSettings } = this.state

    if (!item || !fieldSettings) {
      return <Spin />
    }

    const createLabel = createLabelFactory(fieldSettings)
    const createFieldDecorator = createFieldFactory(this.props.form, item, fieldSettings)

    const readOnly = isReadOnly(this) || Boolean(item.id)

    return (
      <Container>
        <Form layout="vertical" colon={false} onSubmit={readOnly ? stopEvent : this.handleSubmit}>
          <div className="form-items-container">
            <ValidationError errors={this.state.validationErrors} />
            <Form.Item label={createLabel('name')}>
              {createFieldDecorator('name')(<Input readOnly={readOnly} autoFocus />)}
            </Form.Item>
            <Form.Item label={createLabel('templateId')}>
              {createFieldDecorator('templateId')(
                <Select
                  onChange={(value) => this.setItemValue('templateId', value)}
                  placeholder={t('none')}
                  readOnly={readOnly}
                >
                  {(this.state.formTemplates ?? []).map((each) => (
                    <Option key={each.id} value={each.id}>
                      <span {...getOptionProps(each)}>{each.name}</span>
                    </Option>
                  ))}
                </Select>
              )}
            </Form.Item>
            {this.props.tenantIsFnl ? (
              <Form.Item label={createLabel('customerIds')}>
                <div className="tofino-label-buttons">
                  <Button
                    size="small"
                    onClick={() => this.setState({ fileUploadDrawerVisible: true })}
                    disabled={readOnly}
                  >
                    <Icon type="FileUpload" />
                  </Button>
                </div>
                {createFieldDecorator('customerIds')(
                  <TreeSelect
                    onChange={(value) => this.setItemValue('customerIds', value)}
                    dropdownStyle={{ maxHeight: '240px', overflow: 'auto' }}
                    placeholder={t('none')}
                    filterTreeNode={filterTreeNode}
                    disabled={readOnly}
                    maxTagCount={!readOnly ? 20 : undefined}
                    treeCheckable
                    showSearch
                    allowClear
                    multiple
                  >
                    {(this.state.customers ?? []).map((region, regionIndex) => (
                      <TreeNode
                        key={`${region.name}-${regionIndex}`}
                        value={`${region.name}-${regionIndex}`}
                        title={region.name}
                      >
                        {region.districts.map((district, districtIndex) => (
                          <TreeNode
                            key={`${region.name}-${regionIndex}-${district.name}-${districtIndex}`}
                            value={`${region.name}-${regionIndex}-${district.name}-${districtIndex}`}
                            title={district.name}
                          >
                            {district.customers.map((customer) => (
                              <TreeNode key={customer.id} value={customer.id} title={customer.displayName} />
                            ))}
                          </TreeNode>
                        ))}
                      </TreeNode>
                    ))}
                  </TreeSelect>
                )}
              </Form.Item>
            ) : (
              <Form.Item label={createLabel('customerIds')}>
                {createFieldDecorator('customerIds')(
                  <Select
                    onChange={(value) => this.setItemValue('customerIds', value)}
                    placeholder={t('none')}
                    readOnly={readOnly}
                    mode="multiple"
                    allowClear
                  >
                    {(this.state.customers ?? []).map((each) => (
                      <Option key={each.id} value={each.id}>
                        <span {...getOptionProps(each)}>{each.displayName}</span>
                      </Option>
                    ))}
                  </Select>
                )}
              </Form.Item>
            )}
            <Form.Item label={createLabel('dueDate')}>
              {createFieldDecorator('dueDate')(<DatePicker readOnly={readOnly} />)}
            </Form.Item>
            <Form.Item>
              {createFieldDecorator('required', { valuePropName: 'checked' })(
                <Checkbox readOnly={readOnly}>{createLabel('required')}</Checkbox>
              )}
            </Form.Item>
          </div>
          <div className="form-buttons-container">
            {readOnly ? (
              <Button onClick={this.props.onCancel}>{t('close')}</Button>
            ) : (
              <>
                <Tooltip title={t('unsavedChangesWillBeLost')}>
                  <Button onClick={this.props.onCancel}>{t('cancel')}</Button>
                </Tooltip>{' '}
                <Button type="primary" htmlType="submit" loading={this.state.saveAndCloseButtonLoading}>
                  {t('create')}
                </Button>
              </>
            )}
          </div>
        </Form>
        <Drawer
          title={t('selectFile')}
          size="xs"
          visible={this.state.fileUploadDrawerVisible}
          onClose={() => this.setState({ fileUploadDrawerVisible: false })}
        >
          <FileUpload
            accept=".csv,text/csv"
            onUpload={this.handleFileUpload}
            onCancel={() => this.setState({ fileUploadDrawerVisible: false })}
            extra={{
              type: 'info',
              message: t('fileRequirements'),
              description: t('formBatchesUploadInfo'),
            }}
          />
        </Drawer>
      </Container>
    )
  }
}

export default Form.create()(FormView)
